import { createElement } from '../utils';
import { InputValidator } from '../utils/security';
import { FormattingToolbar } from './FormattingToolbar';
import { PreviewModal } from './PreviewModal';

/**
 * Configuration options for MarkdownEditor
 */
interface MarkdownEditorOptions {
  /** Callback when form is submitted */
  onSubmit: (content: string) => void;
  /** Callback when form is cancelled (optional) */
  onCancel?: () => void;
  /** Maximum character length for content */
  maxLength: number;
  /** Theme for the editor */
  theme?: 'light' | 'dark';
  /** Placeholder text for textarea */
  placeholder?: string;
  /** Initial content to populate the editor */
  initialContent?: string;
  /** Label for submit button */
  submitLabel?: string;
  /** Show cancel button */
  showCancelButton?: boolean;
  /** Minimum number of rows for textarea */
  rows?: number;
  /** CSS class name for the container */
  className?: string;
}

/**
 * Reusable Markdown editor component with formatting toolbar and preview
 * Used for creating new comments, replying to comments, and editing existing comments
 */
export class MarkdownEditor {
  private container: HTMLElement;
  private options: Required<MarkdownEditorOptions>;
  private textarea!: HTMLTextAreaElement;
  private submitButton!: HTMLButtonElement;
  private cancelButton?: HTMLButtonElement;
  private characterCount!: HTMLElement;
  private formattingToolbar!: FormattingToolbar;
  private form!: HTMLFormElement;

  constructor(options: MarkdownEditorOptions) {
    this.options = {
      placeholder: 'Write a comment...',
      theme: 'light',
      initialContent: '',
      submitLabel: 'Post Comment',
      showCancelButton: false,
      rows: 3,
      className: 'komentas-markdown-editor',
      onCancel: () => {},
      ...options
    };
    
    this.container = this.createEditor();
    this.setupEventListeners();
    
    // Set initial content if provided
    if (this.options.initialContent) {
      this.setContent(this.options.initialContent);
    }
  }

  /**
   * Create the editor form structure
   */
  private createEditor(): HTMLElement {
    this.form = createElement('form', { className: this.options.className }) as HTMLFormElement;
    
    // Textarea
    this.textarea = createElement('textarea', {
      className: 'komentas-markdown-textarea',
      placeholder: this.options.placeholder,
      rows: this.options.rows.toString(),
      maxlength: this.options.maxLength.toString()
    }) as HTMLTextAreaElement;

    // Create formatting toolbar
    this.formattingToolbar = new FormattingToolbar({
      textarea: this.textarea,
      onPreview: () => this.showPreview(),
      theme: this.options.theme
    });

    // Character counter
    this.characterCount = createElement('div', { 
      className: 'komentas-char-count' 
    }, [`0/${this.options.maxLength}`]);

    // Submit button
    this.submitButton = createElement('button', {
      className: 'komentas-submit-btn',
      type: 'submit'
    }, [this.options.submitLabel]) as HTMLButtonElement;

    // Form controls container
    const controls = createElement('div', { className: 'komentas-form-controls' }, [
      this.characterCount
    ]);

    // Add cancel button if requested
    if (this.options.showCancelButton) {
      this.cancelButton = createElement('button', {
        className: 'komentas-cancel-btn',
        type: 'button'
      }, ['Cancel']) as HTMLButtonElement;
      
      controls.appendChild(this.cancelButton);
    }

    controls.appendChild(this.submitButton);

    // Assemble form: toolbar, textarea, controls
    this.form.appendChild(this.formattingToolbar.getElement());
    this.form.appendChild(this.textarea);
    this.form.appendChild(controls);

    // Add styles
    this.addStyles();

    return this.form;
  }

  /**
   * Setup event listeners for user interactions
   */
  private setupEventListeners(): void {
    // Character counter
    this.textarea.addEventListener('input', () => {
      const length = this.textarea.value.length;
      this.characterCount.textContent = `${length}/${this.options.maxLength}`;
      
      // Update submit button state
      this.updateSubmitButton();
    });

    // Form submission
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSubmit();
    });

    // Cancel button
    if (this.cancelButton) {
      this.cancelButton.addEventListener('click', () => {
        this.options.onCancel();
      });
    }

    // Auto-resize textarea
    this.textarea.addEventListener('input', () => {
      this.textarea.style.height = 'auto';
      this.textarea.style.height = this.textarea.scrollHeight + 'px';
    });
  }

  /**
   * Update submit button enabled/disabled state
   */
  private updateSubmitButton(): void {
    const content = this.textarea.value.trim();
    const isValid = content.length > 0 && content.length <= this.options.maxLength;
    
    this.submitButton.disabled = !isValid;
  }

  /**
   * Handle form submission
   */
  private handleSubmit(): void {
    const content = this.textarea.value.trim();
    
    // Validate comment content using security validator
    const validation = InputValidator.validateComment(content);
    if (!validation.isValid) {
      this.showError(validation.errors.join(', '));
      return;
    }
    
    if (content.length > this.options.maxLength) {
      this.showError(`Content is too long (maximum ${this.options.maxLength} characters)`);
      return;
    }

    // Submit content (Markdown will be rendered when displayed)
    this.options.onSubmit(content);
  }

  /**
   * Show error message
   */
  private showError(message: string): void {
    // Remove existing error messages
    const existingError = this.form.querySelector('.komentas-error-message');
    if (existingError) {
      existingError.remove();
    }

    // Create new error message
    const errorDiv = createElement('div', {
      className: 'komentas-error-message'
    }, [message]);

    // Insert error message after textarea
    this.textarea.parentNode?.insertBefore(errorDiv, this.textarea.nextSibling);

    // Auto-remove error after 5 seconds
    setTimeout(() => {
      errorDiv.remove();
    }, 5000);
  }

  /**
   * Show preview modal
   */
  private showPreview(): void {
    const content = this.textarea.value.trim();
    
    if (!content) {
      this.showError('Nothing to preview. Please enter some text first.');
      return;
    }

    // Show preview modal
    PreviewModal.show(content, this.options.theme);
  }

  /**
   * Add component styles
   */
  private addStyles(): void {
    const isDark = this.options.theme === 'dark';
    
    const styles = `
      .komentas-markdown-editor {
        background: ${isDark ? '#2a2a2a' : '#ffffff'};
        border: 1px solid ${isDark ? '#444' : '#e1e5e9'};
        border-radius: 8px;
        overflow: hidden;
        margin-bottom: 16px;
      }

      .komentas-markdown-editor .komentas-formatting-toolbar {
        border-radius: 0;
        border: none;
        border-bottom: 1px solid ${isDark ? '#444' : '#e1e5e9'};
      }

      .komentas-markdown-textarea {
        width: 100%;
        min-height: 80px;
        max-height: 300px;
        padding: 12px 16px;
        border: none;
        border-radius: 0;
        background: ${isDark ? '#333' : '#fff'};
        color: ${isDark ? '#fff' : '#000'};
        font-family: inherit;
        font-size: 14px;
        line-height: 1.6;
        resize: none;
        overflow-y: auto;
        box-sizing: border-box;
      }

      .komentas-markdown-textarea:focus {
        outline: none;
      }

      .komentas-markdown-textarea::placeholder {
        color: ${isDark ? '#888' : '#666'};
      }

      .komentas-form-controls {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        padding: 12px 16px;
        background: ${isDark ? '#1a1a1a' : '#f5f5f5'};
        border-top: 1px solid ${isDark ? '#444' : '#e1e5e9'};
      }

      .komentas-char-count {
        flex: 1;
        font-size: 12px;
        color: ${isDark ? '#888' : '#666'};
      }

      .komentas-submit-btn,
      .komentas-cancel-btn {
        border: none;
        padding: 8px 16px;
        border-radius: 4px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: background-color 0.2s;
      }

      .komentas-submit-btn {
        background: #007cba;
        color: white;
      }

      .komentas-submit-btn:hover:not(:disabled) {
        background: #005a8b;
      }

      .komentas-submit-btn:disabled {
        background: ${isDark ? '#444' : '#ccc'};
        cursor: not-allowed;
      }

      .komentas-submit-btn:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(0, 124, 186, 0.4);
      }

      .komentas-cancel-btn {
        background: ${isDark ? '#444' : '#e1e5e9'};
        color: ${isDark ? '#fff' : '#333'};
      }

      .komentas-cancel-btn:hover {
        background: ${isDark ? '#555' : '#d0d4d8'};
      }

      .komentas-cancel-btn:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.2);
      }

      .komentas-error-message {
        background: #fee;
        color: #c33;
        border: 1px solid #fcc;
        border-radius: 4px;
        padding: 8px 12px;
        margin: 8px 16px;
        font-size: 14px;
        animation: fadeInError 0.3s ease-in;
      }

      @keyframes fadeInError {
        from { opacity: 0; transform: translateY(-10px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `;

    // Check if styles already exist
    if (!document.getElementById('komentas-markdown-editor-styles')) {
      const styleSheet = createElement('style', { id: 'komentas-markdown-editor-styles' });
      styleSheet.textContent = styles;
      document.head.appendChild(styleSheet);
    }
  }

  /**
   * Get the editor element
   */
  public getElement(): HTMLElement {
    return this.container;
  }

  /**
   * Clear the editor content
   */
  public clear(): void {
    this.textarea.value = '';
    this.characterCount.textContent = `0/${this.options.maxLength}`;
    this.updateSubmitButton();

    // Reset textarea height
    this.textarea.style.height = 'auto';
  }

  /**
   * Focus the textarea
   */
  public focus(): void {
    this.textarea.focus();
  }

  /**
   * Set editor content
   */
  public setContent(content: string): void {
    this.textarea.value = content;
    this.characterCount.textContent = `${content.length}/${this.options.maxLength}`;
    this.updateSubmitButton();
    
    // Trigger auto-resize
    this.textarea.style.height = 'auto';
    this.textarea.style.height = this.textarea.scrollHeight + 'px';
  }

  /**
   * Disable the editor
   */
  public disable(): void {
    this.textarea.disabled = true;
    this.submitButton.disabled = true;
    if (this.cancelButton) {
      this.cancelButton.disabled = true;
    }
  }

  /**
   * Enable the editor
   */
  public enable(): void {
    this.textarea.disabled = false;
    if (this.cancelButton) {
      this.cancelButton.disabled = false;
    }
    this.updateSubmitButton();
  }

  /**
   * Destroy the editor and clean up
   */
  public destroy(): void {
    this.container.remove();
  }
}
